import produce from "immer";

import names, { DashboardActionTypes } from "./dashboardActionTypes";
import {
    DashboardState,
    blankManagerStats,
    blankMemberStats,
    blankManagerJobCounts,
    blankMemberJobCounts,
    blankJobStatistics,
} from "./dashboardState";
import { DashboardType } from "../../../shared/types";

export const dashboardReducer = (
    state: DashboardState,
    action: DashboardActionTypes
): DashboardState => {
    const newState = produce(state, draft => {
        switch (action.type) {
            case names.LOAD_JOB_MANAGER_DETAILS: {
                draft.ui.loadingJobManager = true;
                break;
            }
            case names.LOAD_JOB_MANAGER_DETAILS_SUCCESS: {
                draft.data.jobManagerDetails = action.payload.jobManagerContact;
                draft.ui.loadingJobManager = false;

                draft.ui.error = undefined;
                break;
            }

            case names.LOAD_JOB_MANAGER_DETAILS_FAILURE: {
                draft.ui.error = action.payload.apiError;
                draft.ui.loadingJobManager = false;
                break;
            }

            case names.LOAD_DASHBOARD_TYPE: {
                draft.ui.loadingType = true;
                draft.data.managerStats = blankManagerStats;
                draft.data.memberStats = blankMemberStats;
                //draft.data.dashboardType = DashboardType.None;
                draft.ui.error = undefined;
                draft.data.showAllJobs = action.payload.showAll;
                break;
            }

            case names.LOAD_DASHBOARD_TYPE_SUCCESS: {
                draft.ui.loadingType = false;
                draft.data.dashboardType = action.payload.dashboardType;
                break;
            }

            case names.LOAD_DASHBOARD_TYPE_FAILURE: {
                draft.ui.loadingType = false;
                draft.data.dashboardType = DashboardType.None;
                draft.ui.error = action.payload.error;
                break;
            }

            // manager stats
            case names.LOAD_MANAGER_DASHBOARD_COUNT: {
                draft.ui.managerIsBusy[action.payload.type] = true;

                // set to undefined - we don't know the count yet
                draft.data.managerStats[action.payload.type] = undefined;
                break;
            }

            case names.LOAD_MANAGER_DASHBOARD_COUNT_SUCCESS: {
                draft.ui.managerIsBusy[action.payload.type] = false;
                draft.data.managerStats[action.payload.type] =
                    action.payload.count;
                break;
            }

            case names.LOAD_MANAGER_DASHBOARD_COUNT_FAILURE: {
                draft.ui.managerIsBusy[action.payload.type] = false;

                // set to null - count is not available
                draft.data.managerStats[action.payload.type] = null;
                break;
            }

            case names.LOAD_MANAGER_JOB_STATISTICS: {
                draft.ui.managerIsBusy["stats"] = true;
                draft.data.jobStatistics = blankJobStatistics;
                break;
            }

            case names.LOAD_MANAGER_JOB_STATISTICS_SUCCESS: {
                draft.ui.managerIsBusy["stats"] = false;
                draft.data.jobStatistics = action.payload.stats;
                break;
            }

            case names.LOAD_MANAGER_JOB_STATISTICS_FAILURE: {
                draft.ui.managerIsBusy["stats"] = false;
                draft.data.jobStatistics = blankJobStatistics;
                break;
            }

            // member stats
            case names.LOAD_MEMBER_DASHBOARD_COUNT: {
                draft.ui.memberIsBusy[action.payload.type] = true;

                // set to undefined - we don't know the count yet
                draft.data.memberStats[action.payload.type] = undefined;
                break;
            }

            case names.LOAD_MEMBER_DASHBOARD_COUNT_SUCCESS: {
                draft.ui.memberIsBusy[action.payload.type] = false;
                draft.data.memberStats[action.payload.type] =
                    action.payload.count;
                break;
            }

            case names.LOAD_MEMBER_DASHBOARD_COUNT_FAILURE: {
                draft.ui.memberIsBusy[action.payload.type] = false;

                // set to null - count is not available
                draft.data.memberStats[action.payload.type] = null;
                break;
            }

            // Member Upcoming Interviews
            case names.LOAD_MEMBER_UPCOMING_INTERVIEWS: {
                draft.ui.memberIsBusy.interviewingUpcoming = true;
                draft.data.upcomingMemberInterviews = [];
                break;
            }

            case names.LOAD_MEMBER_UPCOMING_INTERVIEWS_SUCCESS: {
                draft.ui.memberIsBusy.interviewingUpcoming = false;
                draft.data.upcomingMemberInterviews = action.payload.interviews;
                break;
            }

            case names.LOAD_MEMBER_UPCOMING_INTERVIEWS_FAILURE: {
                draft.ui.memberIsBusy.interviewingUpcoming = false;
                draft.data.upcomingMemberInterviews = [];
                break;
            }

            // manager job counts
            case names.LOAD_MANAGER_JOB_COUNTS: {
                draft.ui.managerIsBusy.jobCounts = true;
                draft.data.managerJobCounts = blankManagerJobCounts;
                break;
            }

            case names.LOAD_MANAGER_JOB_COUNTS_SUCCESS: {
                draft.ui.managerIsBusy.jobCounts = false;
                draft.data.managerJobCounts = action.payload.counts;
                break;
            }

            case names.LOAD_MANAGER_JOB_COUNTS_FAILURE: {
                draft.ui.managerIsBusy.jobCounts = false;
                draft.data.managerJobCounts = {
                    closed: null,
                    open: null,
                    paused: null,
                    pending: null,
                    requested: null,
                };
                break;
            }

            // manager job counts
            case names.LOAD_MEMBER_JOB_COUNTS: {
                draft.ui.memberIsBusy.jobCounts = true;
                draft.data.memberJobCounts = blankMemberJobCounts;
                break;
            }

            case names.LOAD_MEMBER_JOB_COUNTS_SUCCESS: {
                draft.ui.memberIsBusy.jobCounts = false;
                draft.data.memberJobCounts = action.payload.counts;
                break;
            }

            case names.LOAD_MEMBER_JOB_COUNTS_FAILURE: {
                draft.ui.memberIsBusy.jobCounts = false;
                draft.data.memberJobCounts = {
                    closed: null,
                    open: null,
                    paused: null,
                };
                break;
            }
        }
        draft.ui.isDashboardLoading = getIsBusy(draft);
    });

    return newState;
};

const getIsBusy = (state: DashboardState) => {
    return (
        state.ui.loadingJobManager ||
        state.ui.loadingType ||
        state.ui.managerIsBusy.hiredConfirmed ||
        state.ui.managerIsBusy.hiredUnconfirmed ||
        state.ui.managerIsBusy.interviewingAwaitingFeedback ||
        state.ui.managerIsBusy.interviewingPast ||
        state.ui.managerIsBusy.interviewingUpcoming ||
        state.ui.managerIsBusy.jobCounts ||
        state.ui.managerIsBusy.offered ||
        state.ui.managerIsBusy.submitted ||
        state.ui.memberIsBusy.interviewingPastAwaitingFeedback ||
        state.ui.memberIsBusy.interviewingUpcoming ||
        state.ui.memberIsBusy.jobCounts ||
        state.ui.memberIsBusy.submitted ||
        state.ui.managerIsBusy.stats
    );
};
